export const metodoPagoEnum = ["efectivo", "transferencia", "tarjeta", "cheque", "otro", "toku"] as const;
export type MetodoPago = (typeof metodoPagoEnum)[number];

export type Pago = {
  id: string;
  folio: string;
  cliente_id: string;
  factura_id: string | null;
  monto: string;
  metodo: MetodoPago;
  referencia: string | null;
  fecha: string;
  anulado: boolean;
  payment_provider?: string | null;  // rc51: 'toku' | null | 'saldo_a_favor'
  created_at: string;
  motivo_anulacion?: string | null;
  anulado_at?: string | null;
  notas?: string | null;
  registrado_por?: string | null;
  registrado_por_nombre?: string | null;
  // rc94: enriquecidos por el backend en GET /pagos (batch lookup, sin N+1)
  cliente_codigo?: string | null;
  cliente_nombre?: string | null;
};

export type PagedPagos = {
  items: Pago[];
  total: number;
  page: number;
  size: number;
  pages: number;
};

// ─── rc50: Detalle extendido del pago ─────────────────────────────────────────
export type EstadoConciliacion = "conciliado" | "saldo_a_favor" | "anulado";
export type OrigenPago =
  | "toku_transfer" | "toku_card" | "toku_cash" | "toku_wallet" | "toku_other"
  | "manual" | "saldo_a_favor";

export type TokuStatusHistoryEntry = {
  timestamp: string | null;
  old_status: string | null;
  new_status: string | null;
};

export type TokuInstrumentCard = {
  bin: string | null;
  card_brand: string | null;
  card_type: string | null;
  card_holder: string | null;
  last_digits: string | null;
  country: string | null;
  issuer: string | null;
  installments_number: number | null;
  three_d_secure: boolean | null;
};

export type TokuInstrumentTransfer = {
  payer_name: string | null;
  payer_clabe: string | null;
  tracking_id: string | null;
  transaction_date: string | null;
  beneficiary_clabe: string | null;
};

export type TokuInstrumentCash = {
  store: string | null;
  voucher: string | null;
  barcode: string | null;
};

export type TokuPaymentIntent = {
  id: string | null;
  amount: string | null;
  currency: string | null;
  status: string | null;
  type: string | null;
  is_partial: boolean | null;
  is_last_payment: boolean | null;
  product_id: string | null;
  invoice_external_id: string | null;
  id_invoice: string | null;
  id_subscription: string | null;
  price_change_amount: string | null;
};

export type TokuWalletMovement = {
  id: string | null;
  type: string | null;
  amount: string | null;
  reason: string | null;
};

export type TokuTransactionDetail = {
  transaction_id: string | null;
  transaction_type: string | null;
  amount: string | null;
  currency: string | null;
  status: string | null;
  transaction_date: string | null;
  initialization_date: string | null;
  response_code: string | null;
  toku_product: string | null;
  gateway: string | null;
  remediation_detail: string | null;
  status_history: TokuStatusHistoryEntry[];
  customer_id: string | null;
  customer_external_id: string | null;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  instrument_type: string | null;
  instrument_card: TokuInstrumentCard | null;
  instrument_transfer: TokuInstrumentTransfer | null;
  instrument_cash: TokuInstrumentCash | null;
  instrument_other: Record<string, unknown> | null;
  payment_intents: TokuPaymentIntent[];
  wallet_movements: TokuWalletMovement[];
  event_id: string | null;
  event_type: string | null;
  received_at: string | null;
  processing_status: string | null;
  processing_error: string | null;
  retry_count: number | null;
  surplus_total: string | null;
};

export type PagoDetalleClienteInfo = {
  id: string;
  codigo: string;
  razon_social: string;
};

export type PagoDetalleFacturaInfo = {
  id: string;
  folio: string;
  monto_total: string;
  saldo: string;
  estatus: string;
};

export type PagoDetalleUserInfo = {
  id: string;
  email: string;
  nombre_completo: string | null;
};

export type PagoDetalle = {
  id: string;
  folio: string;
  monto: string;
  metodo: MetodoPago;
  referencia: string | null;
  fecha: string;
  anulado: boolean;
  motivo_anulacion: string | null;
  anulado_at: string | null;
  payment_provider: string | null;
  payment_provider_payment_id: string | null;
  payment_provider_synced_at: string | null;
  notas: string | null;
  tags: string[];
  created_at: string;
  estado_conciliacion: EstadoConciliacion;
  origen: OrigenPago;
  cliente: PagoDetalleClienteInfo;
  factura: PagoDetalleFacturaInfo | null;
  cajero: PagoDetalleUserInfo | null;
  anulado_por: PagoDetalleUserInfo | null;
  toku: TokuTransactionDetail | null;
};
